import { useEffect, useState } from 'react'
import {
  approveImageModerationCheck,
  blockImageModerationCheck,
  listImageModerationQueue,
  type ImageModerationQueueItem,
} from '../../api/moderation'

export default function ModerationImageQueue() {
  const [items, setItems] = useState<ImageModerationQueueItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  async function load() {
    setLoading(true)
    setError(null)
    try {
      const data = await listImageModerationQueue({ page: 1, page_size: 50 })
      setItems(data.results || [])
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load image queue.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  async function onApprove(id: string) {
    setBusyId(id)
    try {
      await approveImageModerationCheck(id)
      await load()
    } finally {
      setBusyId(null)
    }
  }

  async function onBlock(id: string) {
    setBusyId(id)
    try {
      await blockImageModerationCheck(id)
      await load()
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="space-y-4">
      <div className="text-sm text-white/70">Held image checks for posts, pages, and listings.</div>
      {loading ? <div className="text-sm text-white/70">Loading...</div> : null}
      {error ? <div className="text-sm text-rose-300">{error}</div> : null}
      {!loading && !error && items.length === 0 ? <div className="text-sm text-white/70">Queue is empty.</div> : null}
      <div className="grid gap-3">
        {items.map((row) => (
          <div key={row.id} className="rounded-xl border border-white/10 bg-white/5 p-3">
            <div className="flex flex-wrap items-start gap-3">
              <div className="h-20 w-20 overflow-hidden rounded-lg border border-white/10 bg-black/20">
                {row.thumbnail_url ? <img src={row.thumbnail_url} alt="queued asset" className="h-full w-full object-cover" /> : null}
              </div>
              <div className="flex-1 min-w-[220px]">
                <div className="text-sm font-semibold">{row.content_type}</div>
                <div className="text-xs text-white/70">{row.object_type}:{row.object_id}</div>
                <div className="text-xs text-white/70">{row.target_field}</div>
                <div className="mt-1 text-xs text-amber-200">{row.decision_reason || row.status}</div>
                {Array.isArray(row.categories) && row.categories.length ? (
                  <div className="mt-1 text-xs text-white/70">Categories: {row.categories.join(', ')}</div>
                ) : null}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => onApprove(row.id)}
                  disabled={busyId === row.id}
                  className="rounded-lg border border-emerald-300/40 bg-emerald-500/15 px-3 py-1.5 text-xs font-semibold text-emerald-100 disabled:opacity-60"
                >
                  Approve
                </button>
                <button
                  type="button"
                  onClick={() => onBlock(row.id)}
                  disabled={busyId === row.id}
                  className="rounded-lg border border-rose-300/40 bg-rose-500/15 px-3 py-1.5 text-xs font-semibold text-rose-100 disabled:opacity-60"
                >
                  Block
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
